// src/features/task/TaskActivityFeed.tsx
// Activity feed for the task detail page. Always renders (loading / empty /
// error), unlike the page-level inline block it replaces which swallowed
// ListActivity errors and hid the whole section when empty. Paginated via
// ListActivity's PaginatedRequest — supports "load more".

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, Alert, Badge } from '@react/components';
import type { TaskActivity } from '@core/types/task';
import { formatDateTime, formatActivityType, getDisplayName } from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';

interface TaskActivityFeedProps {
  taskId: string;
}

const PAGE_LIMIT = 20;

export const TaskActivityFeed = observer(({ taskId }: TaskActivityFeedProps) => {
  const store = useTask();
  const { t } = useI18n();

  const [activities, setActivities] = useState<TaskActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState('');
  const [hasNext, setHasNext] = useState(false);

  const load = async (append: boolean) => {
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const res: any = await store.ListActivity(taskId, {
        pagination: { limit: PAGE_LIMIT, cursor: append ? cursor : '', direction: 'forward' },
        sort: [],
      } as any);
      const items: TaskActivity[] = res?.activities?.activities || [];
      setActivities((prev) => (append ? [...prev, ...items] : items));
      const pageInfo = res?.pagination?.page_info;
      setHasNext(!!pageInfo?.has_next);
      setCursor(pageInfo?.next_cursor || '');
    } catch (e) {
      setError(errMsg(e, t('task.activityFeed.loadError')));
      if (!append) setActivities([]);
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('task.activity')}</CardTitle>
      </CardHeader>
      <CardContent>
        {error && <Alert variant="error">{error}</Alert>}

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : activities.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('task.activityFeed.empty')}</p>
        ) : (
          <>
            <div className="divide-y divide-border">
              {activities.map((activity: any, idx: number) => (
                <div key={activity.entity?.id || idx} className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">
                        {getDisplayName(activity.actor)}
                      </span>
                      <Badge variant="secondary" size="sm">
                        {activity.activity_type
                          ? formatActivityType(String(activity.activity_type))
                          : t('task.update')}
                      </Badge>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {formatDateTime(activity.created_at)}
                    </span>
                  </div>
                  {activity.field && (
                    <p className="text-sm text-muted-foreground mt-1">
                      <span className="font-medium">{activity.field}</span>
                      {activity.old_value && <> {t('task.from')} <code className="text-xs bg-muted px-1 rounded">{activity.old_value}</code></>}
                      {activity.new_value && <> {t('task.to')} <code className="text-xs bg-muted px-1 rounded">{activity.new_value}</code></>}
                    </p>
                  )}
                </div>
              ))}
            </div>
            {hasNext && (
              <div className="pt-3 text-center">
                <Button size="sm" variant="secondary" isLoading={loadingMore} onClick={() => load(true)}>
                  {t('common.loadMore')}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
});

export default TaskActivityFeed;
